import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../../api';
import { CalendarDataProvider } from '../Calendar/CalendarDataContext';
import CalendarHome from '../Calendar/CalendarHome';
import CalendarState from '../Calendar/CalendarState';
import CalendarCounty from '../Calendar/CalendarCounty';
import CalendarMonth from '../Calendar/CalendarMonth';

/**
 * The Auction Calendar, inside the tenant admin.
 *
 * =============================================================================
 * THE SCREENS ARE NOT COPIES
 * =============================================================================
 * CalendarHome, CalendarState, CalendarCounty and CalendarMonth are the SAME
 * modules the subscriber app at calendar.yourfirstparcel.com renders. They are
 * imported, not duplicated. What differs is supplied through
 * CalendarDataProvider: where their requests go, and what their links are
 * prefixed with.
 *
 * A copy would have drifted, and it would have drifted invisibly. The month
 * grid alone carries three rules about rolling a passed sale date forward that
 * are impossible to see from the rendered page. A second copy that lost one of
 * them would still look like a calendar. It would just be a different calendar
 * from the one the subscriber sitting next to this tenant at the same auction
 * is paying for.
 *
 * =============================================================================
 * THIS IS NOT THE OTHER AUCTION CALENDAR
 * =============================================================================
 * /admin/auction-calendar is a tenant's OWN private list of auctions it means
 * to attend, stored in the auction_calendar table, one row per tenant. This is
 * the platform's researched county sale calendar, the same data subscribers
 * buy. Similar names, different products, different tables. Nothing here
 * touches that page and nothing there touches this one.
 *
 * =============================================================================
 * READ ONLY, AND ENTITLEMENT IS THE SERVER'S ANSWER
 * =============================================================================
 * This component renders an upgrade page when the server says the tenant is
 * not entitled. It does NOT decide that itself from a tier it read somewhere:
 * the verdict and the sentence both come from GET /admin/calendar/access, the
 * same lib/calendarEntitlement that gates every data route. A browser deciding
 * who is entitled is how a nav item and a route come to disagree, and the one
 * that matters is the route.
 */

const BASE_PATH = '/admin/calendar';

/**
 * The calendar api, in admin clothes.
 *
 * Same contract as the subscriber's calendarApi: takes a path and options,
 * resolves to parsed JSON, throws an Error carrying the server's own message.
 * The screens cannot tell the difference, which is the point.
 *
 * It goes through the shared axios instance, so the admin token and the tenant
 * headers are attached by the interceptor that already exists. That is the
 * opposite of the subscriber client, which deliberately avoids that instance
 * because a subscriber has no tenant; here there IS a tenant and its headers
 * are wanted.
 */
async function adminCalendarApi(path, options = {}) {
  const url = `/admin/calendar${path}`;
  const method = (options.method || 'GET').toUpperCase();

  try {
    const response = method === 'GET'
      ? await api.get(url)
      : await api.request({
        url,
        method,
        data: options.body ? JSON.parse(options.body) : undefined
      });
    return response.data;
  } catch (error) {
    // The server writes every message on these routes to be read by a person,
    // so its message is preferred over the axios one.
    const message = error.response?.data?.error
      || 'Something went wrong. Please try again.';
    const wrapped = new Error(message);
    wrapped.status = error.response?.status;
    wrapped.upgrade = error.response?.data?.upgrade || null;
    throw wrapped;
  }
}

/**
 * What a tenant without the entitlement sees.
 *
 * Short on purpose. It is an upsell, and a long one reads as an apology for a
 * feature they cannot use. The headline and body come from the server so the
 * tier names cannot drift out of step with siteConfig.
 */
function UpgradeNotice({ upgrade }) {
  const navigate = useNavigate();

  return (
    <div className="card" style={{ maxWidth: '560px', padding: '28px' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>📅</div>
      <h2 style={{ marginTop: 0, color: 'var(--color-heading)' }}>
        {(upgrade && upgrade.headline) || 'The Auction Calendar is included with Pro and Enterprise.'}
      </h2>
      <p style={{ color: 'var(--color-text-light)' }}>
        {(upgrade && upgrade.body) || 'Upgrade to see county-by-county sale dates in your admin.'}
      </p>
      <button
        className="btn btn-primary"
        onClick={() => navigate('/admin/account-hub')}
      >
        See plans
      </button>
    </div>
  );
}

function CalendarTabs() {
  const location = useLocation();

  const tab = (to, label) => {
    const active = location.pathname === to
      || (to !== BASE_PATH && location.pathname.startsWith(to));
    return (
      <Link
        to={to}
        style={{
          padding: '8px 2px',
          marginRight: '20px',
          textDecoration: 'none',
          fontWeight: active ? 700 : 500,
          color: active ? 'var(--color-primary)' : 'var(--color-text-light)',
          borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent'
        }}
      >
        {label}
      </Link>
    );
  };

  return (
    <nav style={{
      borderBottom: '1px solid var(--color-border)',
      marginBottom: '20px',
      display: 'flex',
      flexWrap: 'wrap'
    }}>
      {tab(BASE_PATH, 'Upcoming')}
      {tab(`${BASE_PATH}/month`, 'Month')}
    </nav>
  );
}

function AdminCalendar() {
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [liveStates, setLiveStates] = useState([]);
  const [comingSoonStates, setComingSoonStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Stable across renders, so the context value below does not change identity
  // on every render and re-fire the effects in every screen that depends on it.
  const calendarApi = useCallback(adminCalendarApi, []);

  const load = useCallback(async () => {
    try {
      const verdict = await calendarApi('/access');
      setAccess(verdict);

      // Only asked for when the answer is yes. Requesting the state list first
      // would mean an unentitled tenant's upgrade page was preceded by a 403 in
      // the console, which reads as a bug to anybody looking.
      if (verdict.calendarIncluded) {
        const states = await calendarApi('/states');
        setLiveStates(states.liveStates || []);
        setComingSoonStates(states.comingSoonStates || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [calendarApi]);

  useEffect(() => { load(); }, [load]);

  const contextValue = useMemo(() => ({
    api: calendarApi,
    basePath: BASE_PATH
  }), [calendarApi]);

  const header = (
    <div className="admin-hub-header">
      <button
        onClick={() => navigate('/admin')}
        className="admin-hub-back-btn"
      >
        <span className="admin-hub-back-arrow">←</span>
        Back to Dashboard
      </button>
      <h1 className="admin-page-title">Auction Calendar</h1>
      <p className="admin-hub-subtitle">
        Tax deed and tax lien sale dates, county by county, kept current by hand
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        {header}
        <p style={{ color: 'var(--color-text-muted)' }}>Loading the calendar</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard-container">
        {header}
        <p style={{ color: 'var(--color-error-text, var(--color-text))' }}>{error}</p>
      </div>
    );
  }

  if (!access || !access.calendarIncluded) {
    return (
      <div className="admin-dashboard-container">
        {header}
        <UpgradeNotice upgrade={access && access.upgrade} />
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {header}
      <CalendarTabs />
      <CalendarDataProvider api={contextValue.api} basePath={contextValue.basePath}>
        <Routes>
          <Route
            index
            element={<CalendarHome liveStates={liveStates} comingSoonStates={comingSoonStates} />}
          />
          <Route path="month" element={<CalendarMonth />} />
          <Route path="states/:code" element={<CalendarState />} />
          <Route path="counties/:id" element={<CalendarCounty />} />
          <Route path="*" element={<Navigate to={BASE_PATH} replace />} />
        </Routes>
      </CalendarDataProvider>
    </div>
  );
}

export default AdminCalendar;
